/* eslint-disable @typescript-eslint/indent */
import React from 'react';
import cn from '@helpers/className';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ultramarine';
  size?: 'lg' | 'md' | 'sm';
  fullWidth?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | (() => void);
  id?: string;
  name?: string;
  value?: string;
}

type Variant = Record<
  string,
  {
    default: string;
    hover: string;
    pressed: string;
    focus: string;
    disabled: string;
  }
>;

/**
 * ButtonV2 Component Documentation
 *
 * A focused, modern button component that addresses
 * the complexity of the original Button component.
 *
 * 1. Separation of Concerns
 *    - Original Button tried to handle too many use cases (buttons, links, anchors)
 *    - ButtonV2 focuses solely on being a button
 *    - Links and other interactive elements should use their own components
 *
 * 2. Simplified API
 *    - Removed complex prop combinations (type, href, newTab)
 *    - Clear, predefined variants instead of custom color classes
 *    - Standard HTML button attributes for better TypeScript support
 *
 * 3. Modern Implementation
 *    - Consistent with current design system
 *    - Better performance with fewer conditional renders
 */

export default function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = true,
  disabled = false,
  children,
  className = '',
  onClick,
  id,
  name,
  value,
}: ButtonProps) {
  // Base styles shared across all variants
  const baseStyles =
    'flex font-rp-basetica items-center justify-center py-2.5 rounded-solar-sm transition-colors duration-400 ease-custom';

  // Variant-specific styles
  const variantStyles: Variant = {
    primary: {
      default: 'bg-solar-button-primary-default text-solar-inverted-primary',
      hover: 'hover:bg-solar-button-primary-hover text-solar-inverted-primary',
      pressed: 'active:bg-solar-button-primary-pressed text-solar-inverted-primary',
      focus: 'focus:bg-solar-button-primary-focus text-solar-inverted-primary',
      disabled:
        'bg-solar-button-secondary-disabled cursor-not-allowed text-solar-disabled pointer-events-none',
    },
    secondary: {
      default: 'bg-solar-button-secondary-default text-solar-primary border border-solar-button',
      hover: 'hover:bg-solar-button-secondary-hover text-solar-primary border border-solar-button',
      pressed:
        'active:solar-button-secondary-pressed text-solar-primary border border-solar-button',
      focus: 'focus:solar-button-secondary-focus text-solar-primary border border-solar-button',
      disabled:
        'bg-solar-button-secondary-disabled cursor-not-allowed text-solar-solar-secondary border border-solar-primary pointer-events-none',
    },
    ultramarine: {
      default: 'bg-solar-button-ultramarine-default text-solar-inverted-primary',
      hover: 'hover:bg-solar-button-ultramarine-hover text-solar-inverted-primary',
      pressed: 'active:bg-solar-button-ultramarine-pressed text-solar-inverted-primary',
      focus: 'focus:bg-solar-button-ultramarine-focus text-solar-inverted-primary',
      disabled:
        'bg-solar-button-secondary-disabled cursor-not-allowed text-solar-disabled pointer-events-none',
    },
  };

  // Size-specific styles
  const sizeStyles: Record<string, string> = {
    lg: 'h-11 text-base px-spacing-md',
    md: 'h-8.5 text-sm px-spacing-md',
    sm: 'h-7 text-xs px-spacing-sm',
  };

  const classes = cn({
    [baseStyles]: true,
    [variantStyles[variant].default]: !disabled,
    [variantStyles[variant].hover]: !disabled,
    [variantStyles[variant].pressed]: !disabled,
    [variantStyles[variant].focus]: !disabled,
    [variantStyles[variant].disabled]: disabled,
    [sizeStyles[size]]: true,
    'w-full': fullWidth,
    [className]: !!className,
  });

  return (
    <button
      type="button"
      disabled={disabled}
      className={classes}
      onClick={onClick}
      id={id}
      name={name}
      value={value}
    >
      {children}
    </button>
  );
}
